// src/pages/PracticePage/AnswerBox.tsx
import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import RichText from '../../components/RichText.tsx';
import { ensureInlineMath } from '../../utils/latex.ts';
import { Question } from './types.ts';
import {
  choicesAsFlatStrings,
  isMultipleChoiceQuestion,
} from '../../utils/questionChoices.ts';
import {
  formatMarkFraction,
  verdictColor,
  type PracticeMarkResult,
} from '../../utils/markDisplay.ts';

const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

type AnswerBoxProps = {
  q: Question;
  markResult?: PracticeMarkResult | null;
};

const AnswerBox: React.FC<AnswerBoxProps> = ({ q, markResult }) => {
  const isMC = isMultipleChoiceQuestion(q.type, q.choices);
  const choices = choicesAsFlatStrings(q.choices);
  const hasSol = !!q.worked_solution;
  const accentColor = markResult ? verdictColor(markResult.verdict) : 'success.main';

  const correctIdx = q.answer_index ?? null;
  const correctLetter =
    correctIdx != null ? (letters[correctIdx] ?? String((correctIdx as number) + 1)) : null;
  const correctChoiceHtml =
    correctIdx != null && choices[correctIdx] ? choices[correctIdx] : null;

  const shortAnswerLatex = (() => {
    if (isMC) return '';
    const ans = (q.answer ?? '').trim();
    const unit = (q.unit ?? '').trim();
    if (!ans && !unit) return '';
    const combined = unit ? `${ans}\\ ${unit}` : ans;
    return ensureInlineMath(combined);
  })();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderLeft: 4,
        borderLeftColor: accentColor,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Typography variant="subtitle1">Answer</Typography>
        {markResult && markResult.max > 0 && (
          <Typography variant="body2" sx={{ fontWeight: 600, color: accentColor, whiteSpace: 'nowrap' }}>
            {formatMarkFraction(markResult.awarded, markResult.max)}
          </Typography>
        )}
      </Box>

      {markResult?.feedback && (
        <Typography variant="body2" sx={{ mb: 1.25, color: 'text.secondary' }}>
          {markResult.feedback}
        </Typography>
      )}

      {isMC && (
        <Box sx={{ mb: hasSol ? 1.5 : 0.5 }}>
          {correctLetter && (
            <Typography variant="body1" sx={{ mb: correctChoiceHtml ? 0.5 : 0 }}>
              Correct: <strong>{correctLetter}</strong>
            </Typography>
          )}
          {correctChoiceHtml ? (
            <RichText html={correctChoiceHtml} />
          ) : q.answer ? (
            <RichText html={ensureInlineMath(q.answer)} />
          ) : null}
        </Box>
      )}

      {!isMC && (q.answer || q.unit) && (
        <Box sx={{ mb: hasSol ? 1.5 : 0.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Accepted answer
          </Typography>
          <RichText html={shortAnswerLatex} />
        </Box>
      )}

      {q.worked_solution && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Worked solution
          </Typography>
          <RichText html={q.worked_solution} />
        </Box>
      )}

      {!isMC && !q.answer && !q.unit && !q.worked_solution && (
        <Typography variant="body2" color="text.secondary">
          No solution available yet.
        </Typography>
      )}
    </Paper>
  );
};

export default AnswerBox;
